import { isBriefFolder } from "./briefFolder";
import { isSystemFolder } from "../services/folderService";
import type { TreeFolder } from "../types";

export interface MoveDragSelection {
  docIds: string[];
  folderIds: string[];
}

export type MoveDragItem =
  | { kind: "document"; id: string; label: string }
  | { kind: "folder"; id: string; label: string };

function findFolder(folders: TreeFolder[], id: string): TreeFolder | null {
  for (const folder of folders) {
    if (folder.id === id) return folder;
    const child = findFolder(folder.children, id);
    if (child) return child;
  }
  return null;
}

function unique(ids: string[]): string[] {
  return Array.from(new Set(ids));
}

/** A move/upload destination must be the project root or a real writable folder. */
export function isWritableFolderDropTarget(
  folders: TreeFolder[],
  targetFolderId: string | null
): boolean {
  if (targetFolderId === null || targetFolderId === "root") return true;
  const target = findFolder(folders, targetFolderId);
  return !!target && !target.isContainer && !isSystemFolder(target);
}

/** True when moving any source folder to the target would create a cycle. */
export function wouldCreateCircularFolderMove(
  folders: TreeFolder[],
  sourceFolderIds: string[],
  targetFolderId: string | null
): boolean {
  if (!targetFolderId || targetFolderId === "root") return false;

  for (const sourceFolderId of sourceFolderIds) {
    if (sourceFolderId === targetFolderId) return true;

    // Termination does not rest on the data: `buildFolderTree` leaves a parent
    // cycle unreachable from the roots it returns, so `findFolder` resolves
    // nothing and this walk stops — but that is an invariant of another module,
    // and this runs from `isEligibleMoveDropTarget` for every row on every
    // render of a drag, where a spin is a frozen tab rather than a wrong answer.
    const seen = new Set<string>();
    let current = findFolder(folders, targetFolderId);
    while (current?.parentId && !seen.has(current.id)) {
      seen.add(current.id);
      if (current.parentId === sourceFolderId) return true;
      current = findFolder(folders, current.parentId);
    }
  }

  return false;
}

/** Used by every visible hierarchy surface so only destinations a drop can use are advertised. */
export function isEligibleMoveDropTarget(
  folders: TreeFolder[],
  targetFolderId: string | null,
  sourceFolderIds: string[]
): boolean {
  return (
    isWritableFolderDropTarget(folders, targetFolderId) &&
    !wouldCreateCircularFolderMove(folders, sourceFolderIds, targetFolderId)
  );
}

/**
 * Row parts that handle the press themselves — the checkbox, the actions cell,
 * a rename input.
 *
 * `dragstart` fires at the *draggable element*, i.e. the row root, so a
 * handler's own `event.target` can only be searched upwards and never sees
 * which part of the row was actually pressed. The pointer-down target is the
 * only record of that; see `useMoveDragOrigin`.
 */
const SELF_HANDLED_ROW_PARTS =
  '[data-actions], [role="checkbox"], input, textarea, [contenteditable="true"]';

/**
 * Every other control scattered through a row — the tag remove `×`, the edit-tags
 * `+`, the version badge, the preview eye, "show contained files", the download
 * of a parent document. None of them is `draggable`, so the press falls back to
 * the draggable row root: a drift past the browser's ~4px threshold starts a
 * move and the control's `click` never fires.
 *
 * `[role="button"]` is deliberately absent — a folder-tree row carries it on its
 * own draggable root (`FolderItem`), so matching it would refuse every folder
 * drag. The row title is the exception in the other direction: it is a button so
 * a click opens the row, but it is also the row's natural drag handle, the way
 * a file name is in every file manager.
 */
const ROW_CONTROLS = "button, a";
const ROW_DRAG_HANDLE = "[data-row-title]";

/** True when a press landed on a row part that owns it, so no move may start. */
export function isSelfHandledRowPart(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  // Checked first: a rename input sits inside the title, and typing in it is
  // never a drag.
  if (target.closest(SELF_HANDLED_ROW_PARTS)) return true;
  const control = target.closest(ROW_CONTROLS);
  return !!control && !control.closest(ROW_DRAG_HANDLE);
}

/**
 * Drop the folders no surface offers as a drag source — the read-only system
 * folder and container pseudo-folders. A checkbox is not a drag handle: those
 * rows can still be ticked, and the selection they join must not smuggle them
 * into a move started from some other row. Ids the tree can't resolve are kept:
 * an incomplete tree is no evidence a folder is ineligible.
 */
function draggableFolderIds(folders: TreeFolder[], folderIds: string[]): string[] {
  return folderIds.filter((id) => {
    const folder = findFolder(folders, id);
    return !folder || (!folder.isContainer && !isSystemFolder(folder) && !isBriefFolder(folder));
  });
}

/**
 * Provenance for the move payload. `dataTransfer` types are not scoped to an
 * origin: any page can `setData("application/selection", …)`, and a drag from it
 * released on a folder row would otherwise be trusted on its MIME type alone and
 * re-file whatever ids it names. Only a drag this document started may move
 * documents, and this is the one writer of the payload, so it raises the flag.
 *
 * The drag source cannot be the only way to lower it again, for the reason
 * `useMoveDragTeardown` exists: the list virtualises, native auto-scroll
 * unmounts the dragged row, and a detached node's `dragend` never reaches
 * `document`. A flag that outlived one drag would then admit a forged payload
 * from any other page for the rest of the page's life — the exact hole it was
 * added to close. So it ends from the same signals that hook uses: `dragend`
 * and `drop` for a drag that finishes normally, and the first `pointermove`
 * afterwards for one cancelled with Escape or released outside the window,
 * since the browser suppresses pointer events for the whole native drag.
 *
 * `drop` is the one that has to listen in the capture phase and clear a whole
 * task later, rather than simply on the bubble. The drop handlers run on
 * React's root container, which sits *inside* `document`, and each one calls
 * `stopPropagation` on a move — React forwards that to the native event, so a
 * bubble listener here would never run at all. Capture runs before those
 * handlers instead, and they read this flag synchronously, so the clear has to
 * outlast the dispatch they are part of.
 *
 * A microtask does not: for a trusted event the browser runs a microtask
 * checkpoint after every listener callback returns (the JS stack is empty
 * because the dispatch was not begun from script), so a microtask queued here
 * would lower the flag *between* this listener and React's, and every real
 * move drop would be refused. Verified in Chrome. A task is the first hook
 * that runs after the dispatch has finished.
 */
let moveDragInProgress = false;
let pendingDropClear: ReturnType<typeof setTimeout> | null = null;

function cancelPendingDropClear() {
  if (pendingDropClear === null) return;
  clearTimeout(pendingDropClear);
  pendingDropClear = null;
}

/** Lowered only once the drop handlers, which read the flag, have run. */
function endMoveDragProvenanceAfterDrop() {
  cancelPendingDropClear();
  pendingDropClear = setTimeout(endMoveDragProvenance, 0);
}

function endMoveDragProvenance() {
  cancelPendingDropClear();
  moveDragInProgress = false;
  MOVE_DRAG_END_LISTENERS.forEach(([type, listener, capture]) =>
    document.removeEventListener(type, listener, capture)
  );
}

const MOVE_DRAG_END_LISTENERS: ReadonlyArray<
  [type: string, listener: () => void, capture: boolean]
> = [
  ["dragend", endMoveDragProvenance, false],
  ["drop", endMoveDragProvenanceAfterDrop, true],
  ["pointermove", endMoveDragProvenance, true],
];

/** True only while a move drag started in this document is in flight. */
export function isAppMoveDragInProgress(): boolean {
  return moveDragInProgress;
}

/**
 * Write the established native move payload. Dragging one selected row carries
 * the complete selection; dragging an unselected row carries only that row.
 */
export function writeMoveDragPayload(
  dataTransfer: DataTransfer,
  folders: TreeFolder[],
  item: MoveDragItem,
  selection: MoveDragSelection,
  selectionLabel: (count: number) => string
): MoveDragSelection {
  const selectedDocIds = unique(selection.docIds);
  const selectedFolderIds = draggableFolderIds(folders, unique(selection.folderIds));
  const itemIsSelected =
    item.kind === "document"
      ? selectedDocIds.includes(item.id)
      : selectedFolderIds.includes(item.id);
  const selectedCount = selectedDocIds.length + selectedFolderIds.length;

  const payload: MoveDragSelection =
    itemIsSelected && selectedCount > 1
      ? { docIds: selectedDocIds, folderIds: selectedFolderIds }
      : item.kind === "document"
        ? { docIds: [item.id], folderIds: [] }
        : { docIds: [], folderIds: [item.id] };

  if (payload.docIds.length + payload.folderIds.length > 1) {
    dataTransfer.setData("application/selection", JSON.stringify(payload));
    dataTransfer.setData(
      "text/plain",
      selectionLabel(payload.docIds.length + payload.folderIds.length)
    );
  } else if (item.kind === "document") {
    dataTransfer.setData("application/doc-id", item.id);
    dataTransfer.setData("text/plain", item.label);
  } else {
    dataTransfer.setData("application/folder-id", item.id);
    dataTransfer.setData("text/plain", item.label);
  }

  dataTransfer.effectAllowed = "move";
  // A drop's clear is deferred by a task; a drag begun before it lands must not
  // have its own flag lowered by the previous drag's timer.
  cancelPendingDropClear();
  moveDragInProgress = true;
  MOVE_DRAG_END_LISTENERS.forEach(([type, listener, capture]) =>
    document.addEventListener(type, listener, capture)
  );
  return payload;
}

/** The MIME types `writeMoveDragPayload` writes; exactly one per drag. */
const MOVE_DRAG_TYPES = ["application/selection", "application/doc-id", "application/folder-id"];

/** The move payload names ids and nothing else; anything else is not ours. */
export function isIdList(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((id) => typeof id === "string");
}

/**
 * True when the drag carries a move payload that this document wrote. Readable
 * during `dragover`, when the browser exposes the types but not the data.
 */
export function carriesAppMovePayload(dataTransfer: DataTransfer | null | undefined): boolean {
  if (!dataTransfer || !isAppMoveDragInProgress()) return false;
  const types = Array.from(dataTransfer.types);
  return MOVE_DRAG_TYPES.some((type) => types.includes(type));
}

/**
 * Read back what `writeMoveDragPayload` wrote, for a surface that takes the
 * dragged items somewhere other than a folder. Only readable in `drop`, and
 * only trusted while this document's own drag is in flight — the MIME types are
 * forgeable by any page the user might drag from. Null when the drag is not
 * ours or the payload is malformed.
 */
export function readMoveDragPayload(dataTransfer: DataTransfer): MoveDragSelection | null {
  if (!carriesAppMovePayload(dataTransfer)) return null;

  const selection = dataTransfer.getData("application/selection");
  if (selection) {
    try {
      const parsed = JSON.parse(selection) as { docIds?: unknown; folderIds?: unknown } | null;
      const docIds = parsed?.docIds;
      const folderIds = parsed?.folderIds;
      if (!isIdList(docIds) || !isIdList(folderIds)) return null;
      return { docIds: unique(docIds), folderIds: unique(folderIds) };
    } catch {
      return null;
    }
  }

  const docId = dataTransfer.getData("application/doc-id");
  if (docId) return { docIds: [docId], folderIds: [] };

  const folderId = dataTransfer.getData("application/folder-id");
  if (folderId) return { docIds: [], folderIds: [folderId] };

  return null;
}
